import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Checkbox, RadioGroup, RadioGroupItem, cn } from '@bliks-io/ui';
import { formatStatsLine } from '../buildCopy';

export interface BucketRowMeta {
  recommended?: boolean;
  stats?: { occurrences: number; samples: number };
}

export interface BucketSection<T extends { _id: string }> {
  /** Stable key used for React reconciliation. */
  key: string;
  title: string;
  subtitle?: string;
  items: T[];
  /** Optional count for the small badge next to the header. Defaults to items.length. */
  count?: number;
  /** Per-item meta — defaults to {} when not provided. */
  metaFor?: (item: T) => BucketRowMeta;
}

export interface EntityPickerBucketsListProps<T extends { _id: string }> {
  sections: BucketSection<T>[];
  selectedIds: string[];
  /** Toggle (multi) or select (single) the row. */
  onToggle: (id: string) => void;
  mode: 'single' | 'multi';
  /** Renders the entity card body for a row, given the item and its meta. */
  renderItem: (item: T, meta: BucketRowMeta) => ReactNode;
}

export function EntityPickerBucketsList<T extends { _id: string }>({
  sections,
  selectedIds,
  onToggle,
  mode,
  renderItem
}: EntityPickerBucketsListProps<T>) {
  const { t } = useTranslation();
  const selectedSet = new Set(selectedIds);
  const visibleSections = sections.filter((s) => s.items.length > 0);

  const renderRow = (item: T, meta: BucketRowMeta) => {
    const checked = selectedSet.has(item._id);
    const statsLine = meta.stats ? formatStatsLine(t, meta.stats) : null;
    const body = (
      <div className="flex flex-1 min-w-0 flex-col">
        {renderItem(item, meta)}
        {statsLine && <span className="text-xs text-muted-foreground pl-1">{statsLine}</span>}
      </div>
    );
    return (
      <div
        key={item._id}
        role="option"
        aria-selected={checked}
        className={cn(
          'flex items-center gap-3 rounded-md px-2 py-1.5 cursor-pointer hover:bg-accent',
          checked && 'bg-primary/5'
        )}
        onClick={() => onToggle(item._id)}
      >
        {mode === 'multi' ? (
          <Checkbox
            checked={checked}
            onCheckedChange={() => onToggle(item._id)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <RadioGroupItem value={item._id} />
        )}
        {body}
        {meta.recommended && (
          <Badge variant="outline" colorScheme="green" className="self-center">
            {t('entityPicker.common.recommendedBadge')}
          </Badge>
        )}
      </div>
    );
  };

  const renderSection = (section: BucketSection<T>) => {
    const count = section.count ?? section.items.length;
    return (
      <section key={section.key} className="mb-4">
        <div className="sticky top-0 z-10 bg-background mb-2 flex items-baseline justify-between">
          <h3 className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
            <span>{section.title}</span>
            {count > 0 && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-3xs font-medium text-muted-foreground">
                {count}
              </span>
            )}
          </h3>
          {section.subtitle && (
            <span className="text-xs text-muted-foreground">{section.subtitle}</span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {section.items.map((item) => renderRow(item, section.metaFor?.(item) ?? {}))}
        </div>
      </section>
    );
  };

  const sectionContent = <>{visibleSections.map((section) => renderSection(section))}</>;

  return (
    <div>
      {mode === 'single' ? (
        <RadioGroup value={selectedIds[0] ?? ''} onValueChange={(v) => onToggle(v)}>
          {sectionContent}
        </RadioGroup>
      ) : (
        sectionContent
      )}
    </div>
  );
}
